import { useEffect } from "react";
import { Navigate, Outlet, useNavigate, useOutletContext } from "react-router";

import { IdValue, Spinner } from "@prequel/shared-components";

import type { ProductResource } from "@/api/import/products";
import { useProduct } from "@/api/import/products";
import DetailPageLayout from "@/components/import/DetailPageLayout";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type ProductContext = {
  product: ProductResource;
};

export function useProductContext(): ProductContext {
  return useOutletContext<ProductContext>();
}

const ProductPage = () => {
  const productID = useRequiredParam("productID");
  const { addToast } = useToast();
  const { data: product, error, isLoading } = useProduct(productID);

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load product." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!product) {
    return <Navigate to="/import/destinations/products" replace />;
  }

  return <Outlet context={{ product }} />;
};

export const ProductDetailsPage = () => {
  const navigate = useNavigate();
  const { product } = useProductContext();

  return (
    <DetailPageLayout
      title={product.name}
      onGoBack={() => navigate("/import/destinations/products")}
      subheading={
        <div className="flex items-center gap-2">
          <IdValue id={product.id} />
        </div>
      }
    >
      <Outlet context={{ product }} />
    </DetailPageLayout>
  );
};

export default ProductPage;
